import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import { FaThLarge, FaAngleUp, FaAngleDown } from "react-icons/fa";
import { Workflow } from "lucide-react";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupIcon from "@mui/icons-material/Group";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import NavHeader from "../components/nav/NavHeader";
import SideMenu from "../components/nav/Sidemenu";
import RankedTable from "../components/layout/RankedTable";
import DepartmentCard from "../components/layout/DepartmentCard";
import PowerBIIcon from "../components/icons/PowerBIIcon";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";
import "../assets/css/reportRegistrations.css";

const DepartmentPage = () => {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [reports, setReports] = useState([]);
  const [overallLastVisit, setOverallLastVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [reportViewMode, setReportViewMode] = useState("grid");
  const [deptExpanded, setDeptExpanded] = useState(true);
  const [reportsExpanded, setReportsExpanded] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.get(apiUrl.personasWithUsers());
      const raw = res.data?.data ?? [];
      setPersonas(raw);
      setReports(res.data?.reports ?? []);
      setOverallLastVisit({
        lastVisited: res.data?.lastVisited ?? null,
        lastVisitedGroup: res.data?.lastVisitedGroup ?? null,
        lastVisitedReport: res.data?.lastVisitedReport ?? null,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = personas.filter((p) =>
    p.personaName.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredReports = reports.filter(
    (r) =>
      r.reportName.toLowerCase().includes(searchText.toLowerCase()) ||
      (r.groupName || "").toLowerCase().includes(searchText.toLowerCase())
  );

  const goToOverview = (groupId) => navigate(`/overview/${groupId}`);
  const goToReportPage = (groupId,reportId) => navigate(`/reports/${reportId}/view?groupId=${groupId}`);


  const totalReports = personas.reduce((sum, p) => sum + (p.reportCount || 0), 0);

  const lastVisitedDisplay = overallLastVisit?.lastVisited
    ? new Date(overallLastVisit.lastVisited).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="d-flex flex-column min-vh-100" style={{ background: "#f8f9fb" }}>
      <NavHeader />
      <div className="d-flex flex-grow-1">
        <SideMenu />

        <main id="main" className="flex-grow-1" style={{ overflowY: "auto" }}>
          {/* Full-width header banner with gradient + bubble design, overlaid title, subtitle, and counts */}
          <div className="rr-heroBanner" style={{ height: 340 }}>
            <span className="rr-heroBubble rr-heroBubble--1" />
            <span className="rr-heroBubble rr-heroBubble--2" />
            <span className="rr-heroBubble rr-heroBubble--3" />
            <span className="rr-heroBubble rr-heroBubble--4" />
            <span className="rr-heroBubble rr-heroBubble--5" />
            <span className="rr-heroBubble rr-heroBubble--6" />
            <span className="rr-heroRing rr-heroRing--1" />
            <span className="rr-heroRing rr-heroRing--2" />
            <span className="rr-heroRing rr-heroRing--3" />
            <span className="rr-heroDot rr-heroDot--1" />
            <span className="rr-heroDot rr-heroDot--2" />
            <span className="rr-heroDot rr-heroDot--3" />
            <span className="rr-heroDot rr-heroDot--4" />
            <span className="rr-heroDot rr-heroDot--5" />
            <span className="rr-heroDot rr-heroDot--6" />
            <span className="rr-heroDot rr-heroDot--7" />
            <span className="rr-heroDot rr-heroDot--8" />
            <span className="rr-heroDot rr-heroDot--9" />
            <span className="rr-heroDot rr-heroDot--10" />
            <span className="rr-heroDot rr-heroDot--11" />
            <span className="rr-heroDot rr-heroDot--12" />
            <span className="rr-heroDot rr-heroDot--13" />
            <span className="rr-heroDot rr-heroDot--14" />
            <span className="rr-heroDot rr-heroDot--15" />
            <span className="rr-heroDot rr-heroDot--16" />
            <div style={{ ...heroOverlayStyle, height: 340 }}>
              <div style={{ padding: "0 32px" }}>
                <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: "#fff" }}>
                  Department Overview
                </h1>
                <p style={{ margin: "6px 0 20px", fontSize: 14, color: "rgba(255,255,255,0.85)" }}>
                  User groups and their assigned users
                </p>
                {!loading && (
                  <div style={{ display: "flex", gap: 16 }}>
                    <div style={heroBadgeStyle}>
                      <GroupIcon style={{ color: "#fff", fontSize: 20 }} />
                      <span style={{ fontWeight: 700, fontSize: 20, color: "#fff" }}>
                        {personas.length}
                      </span>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>User Groups</span>
                    </div>
                    <div style={heroBadgeStyle}>
                      <DescriptionIcon style={{ color: "#fff", fontSize: 20 }} />
                      <span style={{ fontWeight: 700, fontSize: 20, color: "#fff" }}>
                        {totalReports}
                      </span>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>Total Reports</span>
                    </div>
                    {lastVisitedDisplay && (
                      <div style={heroBadgeStyle}>
                        <AccessTimeIcon style={{ color: "#fff", fontSize: 20 }} />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>
                            {overallLastVisit.lastVisitedReport || "Unknown report"}
                          </span>
                          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)" }}>
                            Last visited · {lastVisitedDisplay}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pt-4 py-6">

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              className="form-control rr-input"
              placeholder="Search user groups or reports…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </div>

          {/* Department section */}
          <section className="relative z-10 rounded-2xl border border-slate-200/80 bg-white/90 p-4 md:p-6 shadow-sm backdrop-blur">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>
                Assigned Persona ({filtered.length})
              </h2>
              <div className="flex items-center gap-2">
                <div style={viewToggleWrapStyle}>
                  <button
                    type="button"
                    title="Grid view"
                    onClick={() => setViewMode("grid")}
                    style={viewMode === "grid" ? viewToggleBtnActiveStyle : viewToggleBtnStyle}
                  >
                    <GridViewIcon style={{ fontSize: 18 }} />
                  </button>
                  <button
                    type="button"
                    title="Table view"
                    onClick={() => setViewMode("table")}
                    style={viewMode === "table" ? viewToggleBtnActiveStyle : viewToggleBtnStyle}
                  >
                    <TableRowsIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setDeptExpanded((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <FaThLarge className="text-slate-500" />
                  {deptExpanded ? (
                    <span>
                      Collapse <FaAngleUp className="ml-1 inline text-slate-500" />
                    </span>
                  ) : (
                    <span>
                      Expand <FaAngleDown className="ml-1 inline text-slate-500" />
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className={deptExpanded ? "block" : "hidden"}>
              {/* KPI card grid / table */}
              {loading ? (
                <div style={gridStyle}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <PersonaKPICardSkeleton key={i} />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  {searchText ? "No user groups match your search." : "No user groups found."}
                </p>
              ) : viewMode === "grid" ? (
                <div style={gridStyle}>
                  {filtered.map((p) => (
                    <PersonaKPICard key={p.userGroupId} persona={p} onClick={() => goToOverview(p.userGroupId)} />
                  ))}
                </div>
              ) : (
                <PersonaTable personas={filtered} onRowClick={(p) => goToOverview(p.userGroupId)} />
              )}
            </div>

            {!deptExpanded && (
              <CollapsedPlaceholder
                icon={<GroupIcon style={{ fontSize: 40, color: "#d1d5db" }} />}
                text="Departments are hidden. Toggle the icon above to view all departments."
              />
            )}
          </section>

          {/* Reports section */}
          <section className="relative z-10 mt-5 rounded-2xl border border-slate-200/80 bg-white/95 p-4 md:p-6 shadow-sm backdrop-blur">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>
              Assigned Reports ({filteredReports.length})
              </h2>
              <div className="flex items-center gap-2">
                <div style={viewToggleWrapStyle}>
                  <button
                    type="button"
                    title="Grid view"
                    onClick={() => setReportViewMode("grid")}
                    style={reportViewMode === "grid" ? viewToggleBtnActiveStyle : viewToggleBtnStyle}
                  >
                    <GridViewIcon style={{ fontSize: 18 }} />
                  </button>
                  <button
                    type="button"
                    title="Table view"
                    onClick={() => setReportViewMode("table")}
                    style={reportViewMode === "table" ? viewToggleBtnActiveStyle : viewToggleBtnStyle}
                  >
                    <TableRowsIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setReportsExpanded((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <FaThLarge className="text-slate-500" />
                  {reportsExpanded ? (
                    <span>
                      Collapse <FaAngleUp className="ml-1 inline text-slate-500" />
                    </span>
                  ) : (
                    <span>
                      Expand <FaAngleDown className="ml-1 inline text-slate-500" />
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className={reportsExpanded ? "block" : "hidden"}>
              {/* Report KPI card grid / table */}
              {loading ? (
                <div style={reportGridStyle}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <ReportKPICardSkeleton key={i} />
                  ))}
                </div>
              ) : filteredReports.length === 0 ? (
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  {searchText ? "No reports match your search." : "No reports found."}
                </p>
              ) : reportViewMode === "grid" ? (
                <div style={reportGridStyle}>
                  {filteredReports.map((r) => (
                    <ReportKPICard key={r.reportId} report={r} onClick={() => goToReportPage(r.groupid,r.reportId)} />
                  ))}
                </div>
              ) : (
                <ReportsTable reports={filteredReports} onRowClick={(r) => goToReportPage(r.groupid,r.reportId)} />
              )}
            </div>

            {!reportsExpanded && (
              <CollapsedPlaceholder
                icon={<DescriptionIcon style={{ fontSize: 40, color: "#d1d5db" }} />}
                text="Reports are hidden. Toggle the icon above to view all reports."
              />
            )}
          </section>
          </div>{/* end p-4 */}
        </main>
      </div>
    </div>
  );
};

const CollapsedPlaceholder = ({ icon, text }) => (
  <div
    className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 text-center"
    style={{ padding: "2.5rem 1rem" }}
  >
    <div
      className="grid h-16 w-16 place-items-center rounded-full"
      style={{ background: "color-mix(in srgb, var(--app-primary-color) 10%, white)" }}
      aria-hidden="true"
    >
      {icon}
    </div>
    <span className="text-sm text-slate-500">{text}</span>
  </div>
);

const PersonaKPICard = ({ persona, onClick }) => {
  const createdAt = persona.createdAt
    ? new Date(persona.createdAt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  return (
    <DepartmentCard
      label={persona.personaName}
      sublabel={persona.createdBy ? `Created by ${persona.createdBy}` : undefined}
      icon={<Workflow size={20} />}
      onClick={onClick}
      linkTo={`/overview/${persona.userGroupId}`}
      recentlyUsed={persona.recentLastUsed}
      stat1Icon={<PowerBIIcon size={17} />}
      stat1Label={persona.reportCount === 1 ? "Report" : "Reports"}
      stat1Value={persona.reportCount ?? 0}
      stat1Color="products"
      stat2Icon={<CalendarTodayIcon style={{ fontSize: 17 }} />}
      stat2Label="Created"
      stat2Value={createdAt}
      stat2Color="assets"
    />
  );
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "18px",
};

const reportGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "18px",
};

const PersonaKPICardSkeleton = () => <DepartmentCardSkeleton />;

const ReportKPICard = ({ report, onClick }) => {
  const createdAt = report.createdAt
    ? new Date(report.createdAt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  return (
    <DepartmentCard
      label={report.reportName}
      sublabel={report.reportDescription || report.groupName}
      icon={<PowerBIIcon size={20} />}
      onClick={onClick}
      linkTo={`/reports/${report.reportId}/view?groupId=${report.groupid}`}
      recentlyUsed={report.recentLastUsed}
      rls={!!report.rls}
      stat1Icon={<VisibilityIcon style={{ fontSize: 17 }} />}
      stat1Label={report.views === 1 ? "View" : "Views"}
      stat1Value={report.views ?? 0}
      stat1Color="products"
      stat2Icon={<PersonOutlineIcon style={{ fontSize: 17 }} />}
      stat2Label="Created At"
      stat2Value={createdAt}
      stat2Color="assets"
    />
  );
};

const ReportKPICardSkeleton = () => <DepartmentCardSkeleton />;

const DepartmentCardSkeleton = () => (
  <div className="pp-card disabled">
    <div className="pp-card-head">
      <Skeleton variant="rounded" width={40} height={40} style={{ borderRadius: 8 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="40%" height={14} />
      </div>
    </div>
    <div className="pp-card-stats">
      <Skeleton variant="rounded" width="100%" height={48} style={{ borderRadius: 8, flex: 1 }} />
      <Skeleton variant="rounded" width="100%" height={48} style={{ borderRadius: 8, flex: 1 }} />
    </div>
  </div>
);

const PersonaTable = ({ personas, onRowClick }) => {
  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const formatDateTime = (value) =>
    value
      ? new Date(value).toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  return (
    <RankedTable
      columns={[
        { label: "Persona Name" },
        { label: "Reports", align: "center" },
        { label: "Created At" },
        { label: "Last Visited Report" },
        { label: "Group" },
        { label: "Date of Last Visited" },
      ]}
      rows={personas}
      emptyText="No user groups found"
      renderRow={(p) => (
        <>
          <td
            className="px-5 py-3"
            style={{ cursor: "pointer" }}
            onClick={() => onRowClick(p)}
          >
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-gray-800">{p.personaName}</span>
              {p.createdBy && <span className="text-[11px] text-gray-400">{p.createdBy}</span>}
            </div>
          </td>
          <td className="px-5 py-3 text-center" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            <span className="inline-flex items-center gap-1 justify-center">
              <DescriptionIcon style={{ fontSize: 16, color: "var(--app-primary-color)" }} />
              {p.reportCount ?? 0}
            </span>
          </td>
          <td className="px-5 py-3 text-gray-500" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            {formatDate(p.createdAt)}
          </td>
          <td className="px-5 py-3 text-gray-600" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            {p.lastVisitedReport || "—"}
          </td>
          <td className="px-5 py-3 text-gray-600" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            {p.lastVisitedGroup || "—"}
          </td>
          <td className="px-5 py-3 text-gray-500" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            {formatDateTime(p.lastVisitedAt)}
          </td>
        </>
      )}
    />
  );
};

const ReportsTable = ({ reports, onRowClick }) => {
  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  return (
    <RankedTable
      columns={[
        { label: "Report Name" },
        { label: "Description" },
        { label: "Group" },
        { label: "Views", align: "center" },
        { label: "RLS", align: "left" },
        { label: "Created At" },
      ]}
      rows={reports}
      emptyText="No reports found"
      renderRow={(r) => (
        <>
          <td
            className="px-5 py-3"
            style={{ cursor: "pointer" }}
            onClick={() => onRowClick(r)}
          >
            <div className="flex flex-col gap-0.5">
              <span className="inline-flex items-center gap-1.5 font-semibold text-gray-800">
                {r.reportName}
                {r.recentLastUsed && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-700">
                    <AccessTimeIcon style={{ fontSize: 9 }} /> Recently Used
                  </span>
                )}
              </span>
              {r.createdBy && <span className="text-[11px] text-gray-400">{r.createdBy}</span>}
            </div>
          </td>
          <td className="px-5 py-3 text-gray-600" style={{ cursor: "pointer" }} onClick={() => onRowClick(r)}>
            {r.reportDescription || "—"}
          </td>
          <td className="px-5 py-3 text-gray-600" style={{ cursor: "pointer" }} onClick={() => onRowClick(r)}>
            {r.groupName || "—"}
          </td>
          <td className="px-5 py-3 text-center" style={{ cursor: "pointer" }} onClick={() => onRowClick(r)}>
            <span className="inline-flex items-center gap-1 justify-center">
              <VisibilityIcon style={{ fontSize: 14, color: "#6b7280" }} />
              {r.views ?? 0}
            </span>
          </td>
          <td className="px-5 py-3 text-left" style={{ cursor: "pointer" }} onClick={() => onRowClick(r)}>
            {r.rls ? (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 border border-green-200">RLS</span>
            ) : (
              <span className="">-</span>
            )}
          </td>
          <td className="px-5 py-3 text-gray-500" style={{ cursor: "pointer" }} onClick={() => onRowClick(r)}>
            {formatDate(r.createdAt)}
          </td>
        </>
      )}
    />
  );
};

const heroOverlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "flex-end",
  paddingBottom: 36,
  background:
    "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 45%, transparent 100%), " +
    "linear-gradient(120deg, color-mix(in srgb, var(--app-primary-color) 55%, transparent) 0%, transparent 60%)",
};

const heroBadgeStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: 10,
  padding: "8px 16px",
  backdropFilter: "blur(4px)",
};

const viewToggleWrapStyle = {
  display: "flex",
  gap: 2,
  background: "#f3f4f6",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 2,
};

const viewToggleBtnStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "none",
  borderRadius: 6,
  background: "transparent",
  color: "#6b7280",
  cursor: "pointer",
};

const viewToggleBtnActiveStyle = {
  ...viewToggleBtnStyle,
  background: "#fff",
  color: "var(--app-primary-color)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
};

export default DepartmentPage;
